import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { parsePromotion, type HomepagePromotion } from "@/lib/homepage-promotion";

function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

function buildServerPublicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (
          isNewSupabaseApiKey(key) &&
          headers.get("Authorization") === `Bearer ${key}`
        ) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export type HomepageContentDTO = {
  promotion: HomepagePromotion | null;
  liveAnnouncementEnabled: boolean;
  heroBadge: string | null;
  heroTitleLine1: string | null;
  heroTitleAccent: string | null;
  heroTitleLine2: string | null;
  heroSubtitle: string | null;
  primaryButtonText: string | null;
  primaryButtonLink: string | null;
  secondaryButtonText: string | null;
  secondaryButtonLink: string | null;
  whyChooseHeading: string | null;
  whyChooseDescription: string | null;
  founderHeading: string | null;
  founderDescription: string | null;
  mealCategoriesHeading: string | null;
  mealCategoriesDescription: string | null;
  ctaHeading: string | null;
  ctaDescription: string | null;
  ctaButtonText: string | null;
  ctaButtonLink: string | null;
  heroImageUrl: string | null;
  heroImageAlt: string | null;
  heroMediaType: "image" | "video";
  heroVideoUrl: string | null;
  heroVideoMobileUrl: string | null;
  heroPosterUrl: string | null;
  whyChooseBannerUrl: string | null;
  founderImageUrl: string | null;
  trustQualityImageUrl: string | null;
  finalCtaBackgroundUrl: string | null;
  howItWorksEyebrow: string | null;
  howItWorksHeading: string | null;
  howItWorksDescription: string | null;
  howItWorksStep1Title: string | null;
  howItWorksStep1Description: string | null;
  howItWorksStep2Title: string | null;
  howItWorksStep2Description: string | null;
  howItWorksStep3Title: string | null;
  howItWorksStep3Description: string | null;
  howItWorksStep4Title: string | null;
  howItWorksStep4Description: string | null;
  howItWorksImageUrl: string | null;
  howItWorksImageAlt: string | null;
};

function normalize(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

// Public, unauthenticated read. Never throws — returns null on any error
// so the homepage can fall back to hardcoded defaults.
export const getHomepageContent = createServerFn({ method: "GET" }).handler(
  async (): Promise<HomepageContentDTO | null> => {
    try {
      const supabase = buildServerPublicClient();
      const { data, error } = await (supabase
        .from("homepage_content") as any)
        .select(
          "promotion_config, live_announcement_enabled, hero_badge, hero_title_line1, hero_title_accent, hero_title_line2, hero_subtitle, primary_button_text, primary_button_link, secondary_button_text, secondary_button_link, why_choose_heading, why_choose_description, founder_heading, founder_description, meal_categories_heading, meal_categories_description, cta_heading, cta_description, cta_button_text, cta_button_link, hero_image_url, hero_image_alt, hero_media_type, hero_video_url, hero_video_mobile_url, hero_poster_url, why_choose_banner_url, founder_image_url, trust_quality_image_url, final_cta_background_url, how_it_works_eyebrow, how_it_works_heading, how_it_works_description, how_it_works_step1_title, how_it_works_step1_description, how_it_works_step2_title, how_it_works_step2_description, how_it_works_step3_title, how_it_works_step3_description, how_it_works_step4_title, how_it_works_step4_description, how_it_works_image_url, how_it_works_image_alt",
        )
        .eq("id", 1)
        .maybeSingle();
      if (error) {
        console.error("[homepage-content] read failed", error);
        return null;
      }
      if (!data) return null;
      return {
        promotion: parsePromotion(data.promotion_config),
        liveAnnouncementEnabled: data.live_announcement_enabled === true,
        heroBadge: normalize(data.hero_badge),
        heroTitleLine1: normalize(data.hero_title_line1),
        heroTitleAccent: normalize(data.hero_title_accent),
        heroTitleLine2: normalize(data.hero_title_line2),
        heroSubtitle: normalize(data.hero_subtitle),
        primaryButtonText: normalize(data.primary_button_text),
        primaryButtonLink: normalize(data.primary_button_link),
        secondaryButtonText: normalize(data.secondary_button_text),
        secondaryButtonLink: normalize(data.secondary_button_link),
        whyChooseHeading: normalize(data.why_choose_heading),
        whyChooseDescription: normalize(data.why_choose_description),
        founderHeading: normalize(data.founder_heading),
        founderDescription: normalize(data.founder_description),
        mealCategoriesHeading: normalize(data.meal_categories_heading),
        mealCategoriesDescription: normalize(data.meal_categories_description),
        ctaHeading: normalize(data.cta_heading),
        ctaDescription: normalize(data.cta_description),
        ctaButtonText: normalize(data.cta_button_text),
        ctaButtonLink: normalize(data.cta_button_link),
        heroImageUrl: normalize(data.hero_image_url),
        heroImageAlt: normalize(data.hero_image_alt),
        heroMediaType: data.hero_media_type === "video" ? "video" : "image",
        heroVideoUrl: normalize(data.hero_video_url),
        heroVideoMobileUrl: normalize(data.hero_video_mobile_url),
        heroPosterUrl: normalize(data.hero_poster_url),
        whyChooseBannerUrl: normalize(data.why_choose_banner_url),
        founderImageUrl: normalize((data as { founder_image_url?: string | null }).founder_image_url),
        trustQualityImageUrl: normalize(data.trust_quality_image_url),
        finalCtaBackgroundUrl: normalize(data.final_cta_background_url),
        howItWorksEyebrow: normalize(data.how_it_works_eyebrow),
        howItWorksHeading: normalize(data.how_it_works_heading),
        howItWorksDescription: normalize(data.how_it_works_description),
        howItWorksStep1Title: normalize(data.how_it_works_step1_title),
        howItWorksStep1Description: normalize(data.how_it_works_step1_description),
        howItWorksStep2Title: normalize(data.how_it_works_step2_title),
        howItWorksStep2Description: normalize(data.how_it_works_step2_description),
        howItWorksStep3Title: normalize(data.how_it_works_step3_title),
        howItWorksStep3Description: normalize(data.how_it_works_step3_description),
        howItWorksStep4Title: normalize(data.how_it_works_step4_title),
        howItWorksStep4Description: normalize(data.how_it_works_step4_description),
        howItWorksImageUrl: normalize(data.how_it_works_image_url),
        howItWorksImageAlt: normalize(data.how_it_works_image_alt),
      };
    } catch (err) {
      console.error("[homepage-content] unexpected error", err);
      return null;
    }
  },
);